import React, { ReactNode, useId, forwardRef } from 'react';
import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { CSSProperties } from '@mui/material';

const computeIsOutOfRange = (
  value: number | null,
  min?: number,
  max?: number,
): boolean => {
  if (value === null) return false;

  return (
    (min !== undefined && value < min) || (max !== undefined && value > max)
  );
};

const clamp = (v: number | null, min?: number, max?: number) => {
  if (v == null) return v;
  let next = v;
  if (min !== undefined && next < min) next = min;
  if (max !== undefined && next > max) next = max;
  return next;
};

export interface S2NumberFieldProps extends BaseNumberField.Root.Props {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: boolean;
  size?: 'small' | 'medium';
  hideArrowButtons?: boolean;
  width?: CSSProperties['width'];
}

export const S2NumberField = forwardRef<HTMLDivElement, S2NumberFieldProps>(
  function S2NumberField(
    {
      label,
      helperText,
      error,
      hideArrowButtons,
      width = '100%',
      size = 'small',
      id: idProp,
      ...rootProps
    },
    ref,
  ) {
    let id = useId();
    if (idProp) {
      id = idProp;
    }

    const { onValueCommitted, min, max } = rootProps;

    const handleValueCommitted: BaseNumberField.Root.Props['onValueCommitted'] =
      (value, details) => {
        const clamped = clamp(value, min, max);
        onValueCommitted?.(clamped, details);
      };

    return (
      <BaseNumberField.Root
        {...rootProps}
        ref={ref}
        id={id}
        onValueCommitted={handleValueCommitted}
        render={(props, state) => {
          const isOutOfRange = computeIsOutOfRange(
            state.value,
            rootProps.min,
            rootProps.max,
          );

          const isError = Boolean(error || isOutOfRange);

          return (
            <FormControl
              size={size}
              ref={props.ref}
              disabled={state.disabled}
              required={state.required}
              error={isError}
              variant="outlined"
            >
              {label && (
                <InputLabel
                  htmlFor={id}
                  sx={{
                    fontFamily: KARLA_FONT_FAMILY,
                    color: ConfigColors.Gray.Primary.raw,

                    // Only when NOT shrunk (inside textbox)
                    '&:not(.MuiInputLabel-shrink)': {
                      fontSize: '0.88rem',
                    },
                  }}
                >
                  {label}
                </InputLabel>
              )}
              {props.children}
            </FormControl>
          );
        }}
      >
        <BaseNumberField.Input
          id={id}
          render={(inputProps, state) => {
            const isOutOfRange = computeIsOutOfRange(
              state.value,
              rootProps.min,
              rootProps.max,
            );

            const isError = Boolean(error || isOutOfRange);
            return (
              <OutlinedInput
                label={label}
                inputRef={inputProps.ref}
                value={state.inputValue}
                onBlur={inputProps.onBlur}
                onChange={inputProps.onChange}
                onKeyUp={inputProps.onKeyUp}
                onKeyDown={inputProps.onKeyDown}
                onFocus={inputProps.onFocus}
                slotProps={{ input: inputProps }}
                endAdornment={
                  !hideArrowButtons ? (
                    <InputAdornment
                      position="end"
                      sx={{
                        flexDirection: 'column',
                        alignSelf: 'stretch',
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                        ml: 0,
                        maxHeight: 'none',
                        '& button': {
                          py: 0,
                          flex: 1,
                          borderRadius: 0.5,
                        },
                      }}
                    >
                      <BaseNumberField.Increment
                        render={
                          <IconButton size={'small'} aria-label="Increase" />
                        }
                      >
                        <KeyboardArrowUpIcon fontSize={'small'} />
                      </BaseNumberField.Increment>

                      <BaseNumberField.Decrement
                        render={
                          <IconButton size={'small'} aria-label="Decrease" />
                        }
                      >
                        <KeyboardArrowDownIcon fontSize={'small'} />
                      </BaseNumberField.Decrement>
                    </InputAdornment>
                  ) : undefined
                }
                sx={{
                  pr: 0,
                  width,
                  transition: 'border-color 150ms ease',

                  '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                    transition: 'border-color 150ms ease',
                    borderWidth: 1,
                    borderColor: isError
                      ? ConfigColors.Raspberry.Primary.raw
                      : ConfigColors.LightGray.Primary.raw,
                  },

                  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: isError
                        ? ConfigColors.Raspberry.Primary.raw
                        : ConfigColors.Royal.Primary.raw,
                    },

                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isError
                      ? ConfigColors.Raspberry.Primary.raw
                      : ConfigColors.Royal.Primary.raw,
                  },

                  '& .MuiInputBase-input': {
                    boxShadow: 'none !important',
                    padding: size === 'small' ? '8.5px 14px' : '16.5px 14px',
                  },

                  '& .MuiInputBase-input:focus': {
                    boxShadow: 'none !important',
                  },

                  '& .MuiInputBase-input:-webkit-autofill': {
                    boxShadow: '0 0 0 1000px transparent inset !important',
                    WebkitBoxShadow:
                      '0 0 0 1000px transparent inset !important',
                  },

                  '&.Mui-disabled': {
                    backgroundColor: ConfigColors.LightGray.Secondary.raw,
                  },
                }}
              />
            );
          }}
        />

        {helperText && (
          <FormHelperText
            sx={{
              ml: 0,
              fontSize: '0.88rem',
              fontFamily: KARLA_FONT_FAMILY,
              color: ConfigColors.Gray.Primary.raw,
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </BaseNumberField.Root>
    );
  },
);
